import { AxiosError, AxiosResponse } from 'axios';
import axios, { authClient, setCountryCode, setAcceptLanguage } from 'utils/axios.config';
import { types, flow, Instance } from 'mobx-state-tree';
import { withAlertStore } from 'components/Core/CustomAlert/store';
import { navBarItems, routes, redirectUrls } from 'containers/Router/routesList';
import { getToken } from 'utils/tokenUtils';
import { saveToken, removeToken } from 'utils/tokenUtils';
import i18n from '../../i18n';
import { ErrorData, ICountryCodeData, IResponse, LangType } from 'types/common';
import { IProfileData, LoginRequest, IProfile, IForgotPassword } from './types';

const { model, maybe, string, frozen, maybeNull, boolean } = types;

export const MainStore = model('MainStore', {
  user: maybe(frozen<IProfile>()),
  token: maybe(string),
  error: maybe(maybeNull(frozen<IResponse<ErrorData>>())),
  globalLoading: boolean,
  forgotPass: maybe(frozen<IForgotPassword>()),
  mobileLanguages: maybeNull(frozen<string[]>()),
})
  .extend(withAlertStore)
  .views((self) => ({
    /** Logged in, but the profile has no selected country (none available, or the profile request was rejected). */
    get hasNoCountry() {
      return !!self.token && !self.user?.selectedCountryCode?.countryCode;
    },
    get redirectUrls() {
      return self.user?.role ? redirectUrls[self.user?.role] : '/';
    },
    get userRoutes() {
      return self.user?.role ? routes[self.user?.role] : routes.all;
    },
    get userNavItems() {
      return self.user?.role ? navBarItems[self.user.role] : [];
    },
  }))
  .actions((self) => ({
    setProfile: flow(function* (data: IProfile) {
      self.user = data;
      self.alertStore.hideAlert();
      yield i18n.changeLanguage(data.lang);
      setAcceptLanguage(data.lang);
      // No selected country (the admin has no available one): the Router shows NoCountries.
      setCountryCode(data.selectedCountryCode?.countryCode ?? '');
      self.globalLoading = false;
    }),
    setMobileLanguages(data: string[]) {
      self.mobileLanguages = data;
    },
  }))
  .actions((self) => ({
    normalizeProfile: flow(function* ({ data: { data } }: AxiosResponse<IResponse<IProfileData>>) {
      self.setProfile(data.user);
      self.token = data.accessToken;
      saveToken(data.accessToken);
    }),
    removeProfile(): void {
      self.user = undefined;
      self.token = '';
      removeToken();
    },
    errorHandling({ response }: AxiosError<IResponse<ErrorData>>) {
      self.error = response?.data;
      const msg = response?.data.message || 'Internal server error';
      self.alertStore.showAlert(msg);
      throw new Error(msg);
    },
    removeError() {
      self.error = null;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      const token = getToken();
      if (token) {
        self.token = token;
        self.globalLoading = true;
        this.getUser();
      }
    },
    getUser: flow(function* () {
      yield authClient
        .get('profile')
        .then(({ data: { data } }) => self.setProfile(data))
        .catch((e) => {
          // A rejected profile (e.g. "no available countries") must not leave the app on the loader.
          self.globalLoading = false;
          self.errorHandling(e);
        });
    }),
    // eslint-disable-next-line require-yield
    login: flow(function* (data: LoginRequest) {
      yield authClient
        .post('auth/login', data, { withCredentials: true })
        .then(self.normalizeProfile)
        .catch(self.errorHandling);
    }),
    logout: flow(function* () {
      yield authClient.post('auth/logout', { token: self.token }).then(self.removeProfile).catch(self.errorHandling);
    }),
    changeLanguage: flow(function* (lang: LangType) {
      yield authClient.patch('profile/change-lang', { lang }).then(self.normalizeProfile).catch(self.errorHandling);
    }),
    changeCountryCode: flow(function* ({ countryCode }: ICountryCodeData) {
      yield authClient
        .patch('profile/change-country-code', { countryCode })
        .then(self.normalizeProfile)
        .catch(self.errorHandling);
    }),
    getMobileLanguagesList: flow(function* () {
      yield axios
        .get<IResponse<string[]>>('point/categories/languages-for-app-list')
        .then(({ data: { data } }) => self.setMobileLanguages(data))
        .catch(self.errorHandling);
    }),
  }));

export type IMainStore = Instance<typeof MainStore>;
